import { useRef,  useState, useEffect } from 'react';
import Topbar from '../components/Topbar';
import { getRoutes, createRoute, updateRoute, deleteRoute, updateRouteStatus, syncFlightsData } from '../services/routesService';
import { getAirports } from '../services/airportService';
import { getAirlines } from '../services/airlineService';
import SearchableAirportSelect from '../components/SearchableAirportSelect';
import { toast } from 'react-toastify';
import Spinner from '../components/Spinner';
import { useRefresh } from '../utils/RefreshContext';

const Svg = ({ d, ...props }) => (
  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="var(--grey)" strokeWidth="1.8" strokeLinecap="round" {...props}>
    <path d={d} />
  </svg>
);

const Routes = () => {
  const { refreshKey } = useRefresh();
  const [routes, setRoutes] = useState([]);
  const [airports, setAirports] = useState([]);
  const [airlines, setAirlines] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isEditing, setIsEditing] = useState(false);
  const [currentId, setCurrentId] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [showForm, setShowForm] = useState(false);

  // Custom Modal State
  const [showConfirm, setShowConfirm] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [showSyncModal, setShowSyncModal] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [syncFormData, setSyncFormData] = useState({
    airport: '',
    iata: '',
    type: 'departure',
    date: new Date().toISOString().split('T')[0]
  });

  // Pagination & Filter States
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [search, setSearch] = useState('');
  const [filterAirline, setFilterAirline] = useState('All');
  const [filterStatus, setFilterStatus] = useState('All');
  const [totalPages, setTotalPages] = useState(1);
  const [totalRecords, setTotalRecords] = useState(0);

  const [formData, setFormData] = useState({
    type: 'departure',
    origin_iata: '',
    destination_iata: '',
    airline_iata: '',
    destination_name: '',
    daily_flights: 1,
    status: 1
  });

  useEffect(() => {
    const fetchOptions = async () => {
      try {
        const [apRes, alRes] = await Promise.all([
          getAirports({ status: 1 }),
          getAirlines({ limit: 1000 })
        ]);
        setAirports(apRes?.data || []);
        setAirlines(alRes?.data || []);
      } catch (err) { console.error(err); }
    };
    fetchOptions();
  }, []);
  const prevSearch = useRef(search);

  useEffect(() => {
    const searchChanged = prevSearch.current !== search;
    prevSearch.current = search;

    const timer = setTimeout(() => {
      if (searchChanged && page !== 1) {
        setPage(1);
      } else {
        fetchData();
      }
    }, searchChanged ? 500 : 0);

    return () => clearTimeout(timer);
  }, [page, limit, filterStatus, filterAirline, search, refreshKey]);


  const fetchData = async () => {
    setLoading(true);
    try {
      const params = {
        page,
        limit,
        search: search || undefined,
        airline_iata: filterAirline === 'All' ? undefined : filterAirline,
        status: filterStatus === 'All' ? undefined : (filterStatus === 'Active' ? 1 : 2)
      };
      const res = await getRoutes(params);
      setRoutes(res?.data || []);
      setTotalPages(res?.meta?.totalPages || 1);
      setTotalRecords(res?.meta?.total || 0);
    } catch (error) {
      console.error('Fetch error:', error);
      toast.error("Failed to load routes");
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (r) => {
    setIsEditing(true);
    setCurrentId(r.id);
    setFormData({
      type: r.type || 'departure',
      origin_iata: r.origin || '',
      destination_iata: r.destination || '',
      airline_iata: r.airline?.iata || '',
      destination_name: r.destination_name || '',
      daily_flights: r.daily_flights || 1,
      status: Number(r.status) || 1
    });
    setShowForm(true);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const confirmDelete = (r) => {
    setDeleteTarget(r);
    setShowConfirm(true);
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    try {
      await deleteRoute(deleteTarget.id, deleteTarget.type);
      toast.success("Route deleted successfully");
      setShowConfirm(false);
      setDeleteTarget(null);
      fetchData();
    } catch (error) {
      toast.error("Delete failed");
    }
  };

  const handleToggleStatus = async (r) => {
    try {
      const newStatus = Number(r.status) === 1 ? 2 : 1;
      await updateRouteStatus(r.id, { status: newStatus, type: r.type });
      toast.success(`Route ${newStatus === 1 ? 'unblocked' : 'blocked'} successfully`);
      fetchData();
    } catch (error) {
      console.log('Failed to update status :', error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.origin_iata || !formData.destination_iata || !formData.airline_iata || !formData.destination_name) {
      return toast.warning("Please fill required fields");
    }

    setSubmitting(true);
    try {
      const response = isEditing
        ? await updateRoute(currentId, formData.type, formData)
        : await createRoute(formData);

      if (response && (response.status === 200 || response.status === 201)) {
        toast.success(`Route ${isEditing ? 'updated' : 'created'} successfully`);
        resetForm();
        fetchData();
      }
    } catch (error) {
      console.error(error);
      toast.error("Something went wrong");
    } finally {
      setSubmitting(false);
    }
  };

  const handleSyncSubmit = async (e) => {
    e.preventDefault();
    const airportCode = syncFormData.airport || syncFormData.iata;
    if (!airportCode) return toast.warning("Please select an airport");

    setSyncing(true);
    try {
      const payload = {
        iata: airportCode,
        type: syncFormData.type,
        date: syncFormData.date || new Date().toISOString().split('T')[0]
      };
      const response = await syncFlightsData(payload);
      if (response && (response.status === 200 || response.status === 201)) {
        toast.success("Flights synced successfully from Cirium");
        setShowSyncModal(false);
        fetchData();
      } else {
        toast.error(response?.data?.message || "Sync failed");
      }
    } catch (error) {
      console.error(error);
      toast.error("Something went wrong during sync");
    } finally {
      setSyncing(false);
    }
  };

  const resetForm = () => {
    setFormData({
      type: 'departure',
      origin_iata: '',
      destination_iata: '',
      airline_iata: '',
      destination_name: '',
      daily_flights: 1,
      status: 1
    });
    setIsEditing(false);
    setCurrentId(null);
    setShowForm(false);
  };

  const renderPagination = () => {
    const pages = [];
    if (totalPages <= 7) {
      for (let i = 1; i <= totalPages; i++) pages.push(i);
    } else {
      if (page <= 4) {
        for (let i = 1; i <= 5; i++) pages.push(i);
        pages.push('...');
        pages.push(totalPages);
      } else if (page >= totalPages - 3) {
        pages.push(1);
        pages.push('...');
        for (let i = totalPages - 4; i <= totalPages; i++) pages.push(i);
      } else {
        pages.push(1);
        pages.push('...');
        pages.push(page - 1);
        pages.push(page);
        pages.push(page + 1);
        pages.push('...');
        pages.push(totalPages);
      }
    }
    return pages;
  };

  if (loading && routes.length === 0) return <Spinner />;

  return (
    <div>
      <Topbar title="Route Management" subtitle="Manage flight routes and synchronize with aviation databases" />

      {/* CUSTOM CONFIRMATION MODAL */}
      {showConfirm && (
        <div className="modal-overlay" style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 999, backdropFilter: 'blur(4px)' }}>
          <div className="modal-card" style={{ background: 'white', padding: 32, borderRadius: 20, width: '100%', maxWidth: 400, textAlign: 'center', boxShadow: '0 20px 25px -5px rgba(0,0,0,0.1)' }}>
            <div style={{ width: 64, height: 64, background: '#FFF5F5', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 20px' }}>
              <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="#F87171" strokeWidth="2"><path d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" /></svg>
            </div>
            <h3 style={{ fontSize: 18, fontWeight: 700, color: 'var(--dark)', marginBottom: 10 }}>Confirm Deletion</h3>
            <p style={{ fontSize: 14, color: 'var(--grey)', marginBottom: 24, lineHeight: 1.5 }}>
              Are you sure you want to delete this route? This action cannot be undone.
            </p>
            <div style={{ display: 'flex', gap: 12 }}>
              <button className="btn btn-outline" style={{ flex: 1 }} onClick={() => setShowConfirm(false)}>Cancel</button>
              <button className="btn btn-primary" style={{ flex: 1, background: '#F87171', borderColor: '#F87171' }} onClick={handleDelete}>Yes, Delete</button>
            </div>
          </div>
        </div>
      )}

      {/* SYNC CIRIUM MODAL */}
      {showSyncModal && (
        <div className="modal-overlay" style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 999, backdropFilter: 'blur(4px)' }}>
          <div className="modal-card" style={{ background: 'white', padding: 32, borderRadius: 20, width: '100%', maxWidth: 450, boxShadow: '0 20px 25px -5px rgba(0,0,0,0.1)', position: 'relative', overflow: 'hidden' }}>
            {syncing && (
              <div style={{
                position: 'absolute',
                inset: 0,
                background: 'rgba(255, 255, 255, 0.92)',
                borderRadius: 20,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 14,
                zIndex: 20,
                backdropFilter: 'blur(3px)'
              }}>
                <div style={{
                  width: 44,
                  height: 44,
                  border: '3.5px solid #E2E8F0',
                  borderTopColor: 'var(--blue)',
                  borderRadius: '50%',
                  animation: 'spin 0.8s linear infinite'
                }} />
                <div style={{ textAlign: 'center' }}>
                  <div style={{ fontSize: 15, fontWeight: 700, color: 'var(--navy)', marginBottom: 4 }}>
                    Syncing Flights from Cirium...
                  </div>
                  <div style={{ fontSize: 12, color: 'var(--grey)' }}>
                    Fetching latest flight schedules. Please wait.
                  </div>
                </div>
              </div>
            )}

            <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 20 }}>
              <div style={{ width: 44, height: 44, background: '#EEF6FF', borderRadius: '12px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="var(--blue)" strokeWidth="2"><polyline points="23 4 23 10 17 10" /><polyline points="1 20 1 14 7 14" /><path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15" /></svg>
              </div>
              <div>
                <h3 style={{ fontSize: 18, fontWeight: 700, color: 'var(--navy)' }}>Sync from Cirium</h3>
                <p style={{ fontSize: 12, color: 'var(--grey)' }}>Fetch latest flight data from Cirium</p>
              </div>
            </div>

            <form onSubmit={handleSyncSubmit}>
              <div className="form-group" style={{ marginBottom: 16 }}>
                <label className="form-label">Airport <span className="req">*</span></label>
                <SearchableAirportSelect
                  value={syncFormData.airport || syncFormData.iata}
                  onChange={(code) => setSyncFormData({ ...syncFormData, airport: code, iata: code })}
                  placeholder="Select Airport"
                  required
                />
              </div>

              <div className="form-group" style={{ marginBottom: 16 }}>
                <label className="form-label">Date <span className="req">*</span></label>
                <input
                  type="date"
                  className="form-input"
                  value={syncFormData.date}
                  onChange={e => setSyncFormData({ ...syncFormData, date: e.target.value })}
                  required
                />
              </div>

              <div className="form-group" style={{ marginBottom: 24 }}>
                <label className="form-label">Direction <span className="req">*</span></label>
                <div style={{ display: 'flex', gap: 12 }}>
                  <label style={{ flex: 1, display: 'flex', alignItems: 'center', gap: 10, padding: '12px', border: `1.5px solid ${syncFormData.type === 'departure' ? 'var(--blue)' : '#E8EDF3'}`, borderRadius: 12, cursor: 'pointer', background: syncFormData.type === 'departure' ? 'rgba(102,178,255,0.05)' : 'white' }}>
                    <input type="radio" name="syncType" checked={syncFormData.type === 'departure'} onChange={() => setSyncFormData({ ...syncFormData, type: 'departure' })} style={{ width: 16, height: 16, accentColor: 'var(--blue)' }} />
                    <span style={{ fontSize: 13, fontWeight: 600 }}>Departure</span>
                  </label>
                  <label style={{ flex: 1, display: 'flex', alignItems: 'center', gap: 10, padding: '12px', border: `1.5px solid ${syncFormData.type === 'arrival' ? 'var(--blue)' : '#E8EDF3'}`, borderRadius: 12, cursor: 'pointer', background: syncFormData.type === 'arrival' ? 'rgba(102,178,255,0.05)' : 'white' }}>
                    <input type="radio" name="syncType" checked={syncFormData.type === 'arrival'} onChange={() => setSyncFormData({ ...syncFormData, type: 'arrival' })} style={{ width: 16, height: 16, accentColor: 'var(--blue)' }} />
                    <span style={{ fontSize: 13, fontWeight: 600 }}>Arrival</span>
                  </label>
                </div>
              </div>

              <div style={{ display: 'flex', gap: 12 }}>
                <button type="button" className="btn btn-outline" style={{ flex: 1, justifyContent: 'center' }} onClick={() => setShowSyncModal(false)} disabled={syncing}>Cancel</button>
                <button type="submit" className="btn btn-primary" style={{ flex: 1, justifyContent: 'center', display: 'flex', alignItems: 'center', gap: 8 }} disabled={syncing}>
                  {syncing ? (
                    <>
                      <span className="spin-anim" style={{ display: 'inline-block', width: 14, height: 14, border: '2px solid rgba(255,255,255,0.4)', borderTopColor: 'white', borderRadius: '50%' }} />
                      <span>Syncing...</span>
                    </>
                  ) : (
                    'Start Sync'
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      <div className="content">
        <div className="screen-label">5.2 — Route Management</div>

        <div className="card">
          {/* FORM SECTION MOVED ABOVE TABLE */}
          {(showForm || isEditing) && (
            <div style={{ borderBottom: '2px solid #F0F4F8', padding: '24px 26px', background: '#F8FAFC', borderRadius: '20px 20px 0 0' }}>
              <div className="screen-label" style={{ marginBottom: 18, fontSize: 13, color: 'var(--blue)', fontWeight: 700 }}>{isEditing ? 'Modify Existing Route' : 'Create New Flight Route'}</div>
              <form onSubmit={handleSubmit}>
                <div className="form-grid">
                  <div className="form-group">
                    <label className="form-label">Type <span className="req">*</span></label>
                    <select
                      className="form-select"
                      value={formData.type}
                      onChange={e => setFormData({ ...formData, type: e.target.value })}
                    >
                      <option value="departure">Departure</option>
                      <option value="arrival">Arrival</option>
                    </select>
                  </div>
                  <div className="form-group">
                    <label className="form-label">Origin Airport <span className="req">*</span></label>
                    <select
                      className="form-select"
                      value={formData.origin_iata}
                      onChange={e => setFormData({ ...formData, origin_iata: e.target.value })}
                    >
                      <option value="">Select Airport</option>
                      {airports.map(ap => (
                        <option key={ap.id} value={ap.code}>{ap.code} — {ap.name}</option>
                      ))}
                    </select>
                  </div>
                  <div className="form-group">
                    <label className="form-label">Destination Airport <span className="req">*</span></label>
                    <select
                      className="form-select"
                      value={formData.destination_iata}
                      onChange={e => setFormData({ ...formData, destination_iata: e.target.value })}
                    >
                      <option value="">Select Airport</option>
                      {airports.map(ap => (
                        <option key={ap.id} value={ap.code}>{ap.code} — {ap.name}</option>
                      ))}
                    </select>
                  </div>
                  <div className="form-group">
                    <label className="form-label">Destination City Name <span className="req">*</span></label>
                    <input
                      className="form-input"
                      placeholder="e.g. New York, Los Angeles"
                      value={formData.destination_name}
                      onChange={e => setFormData({ ...formData, destination_name: e.target.value })}
                    />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Airline <span className="req">*</span></label>
                    <select
                      className="form-select"
                      value={formData.airline_iata}
                      onChange={e => setFormData({ ...formData, airline_iata: e.target.value })}
                    >
                      <option value="">Select Airline</option>
                      {airlines.map(al => (
                        <option key={al.id} value={al.iata}>{al.name} ({al.iata})</option>
                      ))}
                    </select>
                  </div>
                  <div className="form-group">
                    <label className="form-label">Daily Flights <span className="opt">Optional</span></label>
                    <input
                      className="form-input"
                      type="number"
                      placeholder="e.g. 3"
                      value={formData.daily_flights}
                      onChange={e => setFormData({ ...formData, daily_flights: e.target.value })}
                    />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Status <span className="req">*</span></label>
                    <div className="toggle-row" style={{ padding: '9px 12px' }}>
                      <div style={{ fontSize: 12, fontWeight: 600 }}>Active in app</div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 7 }}>
                        <span style={{ fontSize: 11, color: 'var(--grey)' }}>Inactive</span>
                        <button
                          type="button"
                          className={`toggle-switch ${Number(formData.status) === 1 ? 'on' : ''}`}
                          onClick={() => setFormData({ ...formData, status: Number(formData.status) === 1 ? 2 : 1 })}
                        />
                        <span style={{ fontSize: 11, fontWeight: 700, color: Number(formData.status) === 1 ? 'var(--green)' : 'var(--grey)' }}>Active</span>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="form-footer" style={{ paddingLeft: 0, paddingRight: 0, marginTop: 20 }}>
                  <button type="button" className="btn btn-outline" onClick={resetForm}>Cancel</button>
                  <button type="submit" className="btn btn-primary" disabled={submitting}>
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z" /><polyline points="17 21 17 13 7 13 7 21" /></svg>
                    {submitting ? 'Saving...' : isEditing ? 'Update Route' : 'Save Route'}
                  </button>
                </div>
              </form>
            </div>
          )}

          <div className="toolbar">
            <div className="search-wrap">
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="var(--grey)" strokeWidth="2"><circle cx="11" cy="11" r="8" /><path d="M21 21l-4.35-4.35" /></svg>
              <input
                className="search-input"
                placeholder="Search routes..."
                value={search}
                onChange={e => setSearch(e.target.value)}
              />
            </div>
            <select
              className="filter-select"
              value={filterAirline}
              onChange={e => setFilterAirline(e.target.value)}
            >
              <option value="All">All Airlines</option>
              {airlines.map(al => (
                <option key={al.id} value={al.iata}>{al.name}</option>
              ))}
            </select>
            <select
              className="filter-select"
              value={filterStatus}
              onChange={e => setFilterStatus(e.target.value)}
            >
              <option value="All">All Statuses</option>
              <option value="Active">Active</option>
              <option value="Inactive">Inactive</option>
            </select>
            <button
              className="btn btn-sync"
              onClick={() => setShowSyncModal(true)}
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <polyline points="23 4 23 10 17 10" />
                <polyline points="1 20 1 14 7 14" />
                <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15" />
              </svg>
              Sync Flights from Cirium
            </button>
            {!showForm && (
              <button className="btn btn-primary" onClick={() => setShowForm(true)}><svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="12" y1="5" x2="12" y2="19" /><line x1="5" y1="12" x2="19" y2="12" /></svg>Add Route</button>
            )}
          </div>

          <table className="tbl">
            <thead>
              <tr>
                <th>Origin</th>
                <th>Destination</th>
                <th>Type</th>
                <th>Route Details</th>
                <th>Airline</th>
                <th>Flights</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan="8" style={{ textAlign: 'center', padding: '40px' }}><Spinner /></td></tr>
              ) : routes.length === 0 ? (
                <tr><td colSpan="8" style={{ textAlign: 'center', padding: '40px', color: 'var(--grey)' }}>No routes found</td></tr>
              ) : (
                routes.map((r) => (
                  <tr key={r.id}>
                    <td><span className="tag">{r.origin}</span></td>
                    <td><span className="tag">{r.destination}</span></td>
                    <td>
                      <span style={{
                        padding: '4px 10px',
                        borderRadius: '8px',
                        fontSize: '10px',
                        fontWeight: 700,
                        textTransform: 'uppercase',
                        letterSpacing: '0.5px',
                        background: r.type === 'departure' ? 'rgba(59, 130, 246, 0.08)' : 'rgba(16, 185, 129, 0.08)',
                        color: r.type === 'departure' ? '#3b82f6' : '#10b981',
                        border: `1px solid ${r.type === 'departure' ? 'rgba(59, 130, 246, 0.15)' : 'rgba(16, 185, 129, 0.15)'}`,
                        display: 'inline-block'
                      }}>
                        {r.type}
                      </span>
                    </td>
                    <td>
                      <div style={{ fontWeight: 600, color: 'var(--navy)', fontSize: 13 }}>
                        {r.origin_name || (r.origin === 'CMH' ? 'John Glenn Columbus International Airport' : r.origin)}
                      </div>
                      <div style={{ fontSize: 11, color: 'var(--grey)' }}>
                        to {r.destination_name || (r.destination === 'CMH' ? 'John Glenn Columbus International Airport' : r.destination)}
                      </div>
                    </td>
                    <td>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <div style={{
                          width: 26,
                          height: 26,
                          borderRadius: 7,
                          background: '#0078D2',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          fontSize: 9,
                          fontWeight: 800,
                          color: 'white'
                        }}>
                          {r.airline?.iata}
                        </div>
                        <span style={{ fontSize: 13 }}>{r.airline?.name}</span>
                      </div>
                    </td>
                    <td style={{ fontWeight: 600, textAlign: 'center' }}>{r.daily_flights || 0}</td>
                    <td><span className={`badge badge-${Number(r.status) === 1 ? 'active' : 'inactive'}`}>{Number(r.status) === 1 ? 'Active' : 'Inactive'}</span></td>
                    <td>
                      <div className="action-btns">
                        <button className="action-btn" title={Number(r.status) === 1 ? "Block" : "Unblock"} onClick={() => handleToggleStatus(r)}>
                          {Number(r.status) === 1 ? (
                            <Svg d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                          ) : (
                            <Svg d="M8 11V7a4 4 0 118 0m-4 8v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2z" />
                          )}
                        </button>
                        <button className="action-btn" onClick={() => handleEdit(r)}><Svg d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" /></button>
                        <button className="action-btn danger" onClick={() => confirmDelete(r)}><Svg d="M3 6h18M19 6l-1 14H6L5 6M10 11v6M14 11v6M9 6V4h6v2" /></button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>

          {/* PAGINATION */}
          {routes.length > 0 && (
            <div className="pagination-footer" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '16px 26px', borderTop: '1px solid #F0F4F8' }}>
              <div style={{ fontSize: 13, color: 'var(--grey)' }}>
                Showing <span style={{ fontWeight: 700, color: 'var(--dark)' }}>{((page - 1) * limit) + 1}</span> to <span style={{ fontWeight: 700, color: 'var(--dark)' }}>{Math.min(page * limit, totalRecords)}</span> of <span style={{ fontWeight: 700, color: 'var(--dark)' }}>{totalRecords}</span> entries
              </div>
              <div style={{ display: 'flex', gap: 8 }}>
                <button
                  className="btn btn-outline"
                  disabled={page === 1}
                  onClick={() => setPage(page - 1)}
                  style={{ padding: '6px 12px', fontSize: 12 }}
                >
                  Previous
                </button>
                <div style={{ display: 'flex', gap: 4 }}>
                  {renderPagination().map((p, i) => (
                    <button
                      key={i}
                      className={`btn ${page === p ? 'btn-primary' : 'btn-outline'}`}
                      onClick={() => typeof p === 'number' && setPage(p)}
                      style={{
                        padding: '6px 12px',
                        fontSize: 12,
                        minWidth: 36,
                        cursor: typeof p === 'number' ? 'pointer' : 'default',
                        border: typeof p === 'number' ? '' : 'none',
                        background: typeof p === 'number' ? '' : 'transparent'
                      }}
                    >
                      {p}
                    </button>
                  ))}
                </div>
                <button
                  className="btn btn-outline"
                  disabled={page === totalPages}
                  onClick={() => setPage(page + 1)}
                  style={{ padding: '6px 12px', fontSize: 12 }}
                >
                  Next
                </button>
              </div>
            </div>
          )}

        </div>
      </div>
    </div>
  );
};

export default Routes;
